// DOM helpers and dialogs for the web app, drawn with the same shadcn recipes
// in web/styles.css (.cthd-btn, .cthd-badge, .cthd-modal ...). Icons are Lucide.
import {
  createElement, Folder, FolderOpen, FolderPlus, FolderInput, Plus, Upload, Download, Search, SearchX, ChevronRight, ChevronDown,
  ChevronUp, Ellipsis, Link, Image, Undo2, Redo2, Keyboard, Pencil, CopyPlus, Trash2, X, ArrowUp, ArrowDown, ArrowDownUp,
  TriangleAlert, Settings, LogOut, PanelLeft, PanelRight, RefreshCw, Check, Cloud, Copy, ExternalLink, Menu as MenuIcon, WifiOff,
  LogIn, RotateCw, Network, CornerDownRight, ListPlus, Palette, Shapes, Bold, Italic, ALargeSmall, SquareCheckBig, StickyNote,
  Spline, ChevronsDownUp, ChevronsUpDown, Focus, ZoomIn, ZoomOut, Maximize, Minus, Lock, LockOpen, ListTree, LayoutTemplate,
  FileText, ArrowLeftRight, Type, CirclePlus, Scan, Waypoints, FileDown, FileUp, BetweenHorizontalStart, LocateFixed, Rows3,
  Orbit, Pin, PinOff, Share2, Shuffle,
} from 'lucide';

const ICONS = {
  folder: Folder, 'folder-open': FolderOpen, 'folder-plus': FolderPlus, 'folder-input': FolderInput, plus: Plus, upload: Upload,
  download: Download, search: Search, 'search-x': SearchX, 'chevron-right': ChevronRight, 'chevron-down': ChevronDown, 'chevron-up': ChevronUp,
  'more-horizontal': Ellipsis, link: Link, image: Image, 'undo-2': Undo2, 'redo-2': Redo2, keyboard: Keyboard, pencil: Pencil,
  'copy-plus': CopyPlus, 'trash-2': Trash2, x: X, 'arrow-up': ArrowUp, 'arrow-down': ArrowDown, 'arrow-down-up': ArrowDownUp,
  'triangle-alert': TriangleAlert, settings: Settings, 'log-out': LogOut, 'panel-left': PanelLeft, 'panel-right': PanelRight,
  'refresh-cw': RefreshCw, check: Check, cloud: Cloud, copy: Copy, 'external-link': ExternalLink, menu: MenuIcon, 'wifi-off': WifiOff,
  'log-in': LogIn, 'rotate-cw': RotateCw, 'cth-app': Network, // the app's icon (Lucide Network: a topic branching out)
  'corner-down-right': CornerDownRight, 'list-plus': ListPlus, palette: Palette, shapes: Shapes, bold: Bold, italic: Italic,
  'a-large-small': ALargeSmall, 'square-check-big': SquareCheckBig, 'sticky-note': StickyNote, spline: Spline,
  'chevrons-down-up': ChevronsDownUp, 'chevrons-up-down': ChevronsUpDown, focus: Focus, 'zoom-in': ZoomIn, 'zoom-out': ZoomOut,
  maximize: Maximize, minus: Minus, lock: Lock, 'lock-open': LockOpen, 'list-tree': ListTree, 'layout-template': LayoutTemplate,
  'file-text': FileText, 'arrow-left-right': ArrowLeftRight, type: Type, 'circle-plus': CirclePlus, scan: Scan, waypoints: Waypoints,
  'file-down': FileDown, 'file-up': FileUp, 'between-horizontal-start': BetweenHorizontalStart, 'locate-fixed': LocateFixed, 'rows-3': Rows3,
  orbit: Orbit, pin: Pin, 'pin-off': PinOff, 'share-2': Share2, shuffle: Shuffle,
};

export function h(tag, attrs = {}, ...kids) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className = v;
    else if (k === 'text') el.textContent = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, String(v));
  }
  for (const c of kids.flat()) if (c != null && c !== false) el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  return el;
}

export function icon(name, cls = '') {
  const s = h('span', { class: `cthd-ic ${cls}`.trim(), 'aria-hidden': 'true' });
  const node = ICONS[name];
  if (node) s.append(createElement(node));
  return s;
}

/** shadcn Button. Icon-only buttons get their label as the tooltip. */
export function button({ label, iconName, variant = 'outline', size = 'default', onClick, tip, danger, disabled, cls = '' }) {
  const iconOnly = size === 'icon' || size === 'icon-sm';
  const t = iconOnly ? (tip || label) : tip;
  const b = h('button', {
    class: `cthd-btn is-${variant}${size === 'default' ? '' : ` is-${size}`}${danger ? ' is-danger' : ''} ${cls}`.trim(),
    type: 'button', 'aria-label': t || null, title: t || null, disabled: disabled || null,
  });
  if (iconName) b.append(icon(iconName));
  if (!iconOnly && label) b.append(h('span', { text: label }));
  if (onClick) b.addEventListener('click', (e) => onClick(e, b));
  return b;
}

export function badge(text, variant = 'secondary', iconName = null) {
  const b = h('span', { class: `cthd-badge is-${variant}` });
  if (iconName) b.append(icon(iconName));
  b.append(h('span', { text }));
  return b;
}

// ---- toasts ------------------------------------------------------------------

let toastBox = null;
const recent = new Map(); // message -> when it was last shown
// Raw errors in plain English, and the same message twice within 4 s shows once.
const friendly = (t) => String(t)
  .replace(/\((TypeError: )?(Failed to fetch|Load failed|NetworkError[^)]*)\)/g, '(no connection)')
  .replace(/(TypeError: )?(Failed to fetch|Load failed)/g, 'no connection')
  .replace(/NOT_CONNECTED/g, 'Dropbox signed you out');
export function toast(msg, ms = 2600) {
  if (!(msg instanceof Node)) {
    msg = friendly(msg);
    const last = recent.get(msg);
    if (last && Date.now() - last < 4000) return;
    recent.set(msg, Date.now());
  }
  if (!toastBox) { toastBox = h('div', { class: 'cthw-toasts cthd-layer', role: 'status', 'aria-live': 'polite' }); document.body.append(toastBox); }
  const t = h('div', { class: 'cthw-toast' });
  if (msg instanceof Node) t.append(msg); else t.textContent = String(msg);
  const close = () => { t.classList.add('is-out'); setTimeout(() => t.remove(), 200); };
  t.addEventListener('click', (e) => { if (!(e.target instanceof HTMLAnchorElement) && !(e.target instanceof HTMLButtonElement)) close(); });
  toastBox.append(t);
  setTimeout(close, ms);
}

// ---- dialogs -----------------------------------------------------------------

export function isModalOpen() { return !!document.querySelector('.cthw-overlay'); }

/** A shadcn Dialog. Escape and a click outside close it; focus stays inside. */
export function modal(title, desc = '', { wide = false, focusFirst = true } = {}) {
  const content = h('div', { class: 'cthw-modal-content' });
  const box = h('div', { class: `cthd-modal cthw-modal${wide ? ' is-wide' : ''}`, role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    h('div', { class: 'cthw-modal-head' }, h('h2', { class: 'cthw-modal-title', text: title }),
      button({ iconName: 'x', size: 'icon-sm', variant: 'ghost', tip: 'Close', cls: 'cthw-modal-x', onClick: () => api.close() })),
    desc ? h('p', { class: 'cthd-modal-desc', text: desc }) : null,
    content);
  const overlay = h('div', { class: 'cthw-overlay' }, box);
  const before = document.activeElement;
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); api.close(); }
    if (e.key === 'Tab') {
      const f = [...box.querySelectorAll('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')].filter((x) => !x.disabled && x.offsetParent);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  };
  overlay.addEventListener('pointerdown', (e) => { if (e.target === overlay) api.close(); });
  const api = {
    el: box, content, onClose: null, closed: false,
    footer(...buttons) { const f = h('div', { class: 'cthd-modal-footer' }, ...buttons); content.append(f); return f; },
    open() { document.body.append(overlay); window.addEventListener('keydown', onKey, true); setTimeout(() => ((focusFirst && box.querySelector('input, [autofocus]')) || box.querySelector('.cthd-modal-footer .is-default') || box).focus?.(), 0); return api; },
    close() {
      if (api.closed) return;
      api.closed = true;
      window.removeEventListener('keydown', onKey, true);
      overlay.remove();
      before?.focus?.({ preventScroll: true });
      api.onClose?.();
    },
  };
  box.tabIndex = -1;
  return api;
}

export function confirm({ title, body = '', action = 'Delete', danger = true }) {
  return new Promise((res) => {
    const m = modal(title, body);
    const go = button({ label: action, variant: danger ? 'destructive' : 'default', onClick: () => { res(true); m.onClose = null; m.close(); } });
    m.footer(button({ label: 'Cancel', variant: 'outline', onClick: () => m.close() }), go);
    m.onClose = () => res(false);
    m.open();
    setTimeout(() => go.focus(), 0);
  });
}

export function prompt({ title, label = '', value = '', placeholder = '', action = 'Save', desc = '' }) {
  return new Promise((res) => {
    const m = modal(title, desc);
    const input = h('input', { class: 'cthd-input', type: 'text', value, placeholder, spellcheck: 'false', 'aria-label': label || title });
    const finish = (v) => { m.onClose = null; res(v); m.close(); };
    if (label) m.content.append(h('label', { class: 'cthd-label', text: label }));
    m.content.append(input);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); finish(input.value); } });
    m.footer(button({ label: 'Cancel', variant: 'outline', onClick: () => m.close() }), button({ label: action, variant: 'default', onClick: () => finish(input.value) }));
    m.onClose = () => res(null);
    m.open();
    setTimeout(() => { input.focus(); input.select(); }, 0);
  });
}

/** Pick one of `items` ({ label, value }) with a filter box. */
export function pickFrom(title, items, placeholder = 'Type to filter') {
  return new Promise((res) => {
    const m = modal(title);
    const input = h('input', { class: 'cthd-input', type: 'search', placeholder, spellcheck: 'false', 'aria-label': placeholder });
    const list = h('div', { class: 'cthw-picklist', role: 'listbox' });
    let active = 0;
    let shown = items;
    const finish = (v) => { m.onClose = null; res(v); m.close(); };
    const paint = () => {
      const q = input.value.trim().toLowerCase();
      shown = items.filter((i) => i.label.toLowerCase().includes(q));
      active = Math.min(active, Math.max(0, shown.length - 1));
      list.replaceChildren(...shown.map((it, k) => {
        const row = h('button', { class: `cthw-pickrow${k === active ? ' is-active' : ''}`, type: 'button', role: 'option' }, icon(it.icon || 'folder'), h('span', { text: it.label }));
        row.addEventListener('click', () => finish(it.value));
        return row;
      }));
      if (!shown.length) list.append(h('div', { class: 'cthd-hint', text: 'Nothing matches.' }));
    };
    input.addEventListener('input', () => { active = 0; paint(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(shown.length - 1, active + 1); paint(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(0, active - 1); paint(); }
      if (e.key === 'Enter' && shown[active]) { e.preventDefault(); finish(shown[active].value); }
    });
    m.content.append(input, list);
    m.onClose = () => res(null);
    paint();
    m.open();
  });
}

// ---- menus -------------------------------------------------------------------

/** A shadcn DropdownMenu at the pointer. items: { label, icon, run, danger } or 'sep'. */
export function menu(e, items) {
  document.querySelector('.cthw-menu')?.remove();
  const m = h('div', { class: 'cthw-menu cthd-layer', role: 'menu' });
  for (const it of items) {
    if (it === 'sep') { m.append(h('div', { class: 'cthw-menu-sep' })); continue; }
    if (!it) continue;
    const row = h('button', { class: `cthw-menu-item${it.danger ? ' is-danger' : ''}`, type: 'button', role: 'menuitem', disabled: it.disabled || null }, icon(it.checked ? 'check' : it.icon || ''), h('span', { class: 'cthw-menu-label', text: it.label }), it.hint ? h('span', { class: 'cthw-menu-hint', text: it.hint }) : null);
    row.addEventListener('click', () => { close(); it.run(); });
    m.append(row);
  }
  document.body.append(m);
  const r = m.getBoundingClientRect();
  const x = Math.min(e.clientX, innerWidth - r.width - 8);
  const y = Math.min(e.clientY, innerHeight - r.height - 8);
  m.style.left = `${Math.max(8, x)}px`;
  m.style.top = `${Math.max(8, y)}px`;
  const away = (ev) => { if (!m.contains(ev.target)) close(); };
  const key = (ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); close(); } };
  function close() { m.remove(); window.removeEventListener('pointerdown', away, true); window.removeEventListener('keydown', key, true); }
  setTimeout(() => { window.addEventListener('pointerdown', away, true); window.addEventListener('keydown', key, true); }, 0);
  m.querySelector('button')?.focus();
}

// ---- shortcuts and text dialogs -------------------------------------------------------

const MOD = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? 'Cmd' : 'Ctrl';
export const SHORTCUTS = [
  ['Tab', 'Add a child topic'], ['Enter / Shift+Enter', 'Add a sibling topic below / above'], [`${MOD}+Enter`, 'Add a parent topic'],
  ['Type / F2 / Double-click', 'Edit the topic (typing replaces its text)'], ['Enter / Tab (while editing)', 'Done / done and add a child'],
  ['Shift+Enter (while editing)', 'New line in the topic'],
  ['Arrows', 'Move between topics'], ['Alt+Arrows', 'Move the topic up or down among its siblings'],
  ['Shift+Tab / [', 'Move the topic out one level'], [']', 'Move the topic in, under the one above it'],
  ['Space', 'Collapse or expand (Alt+Space: the whole branch)'], ['Delete', 'Delete the topic and its branch'],
  [`${MOD}+Z / Shift+${MOD}+Z`, 'Undo / Redo'], [`${MOD}+C / X / V`, 'Copy / Cut / Paste (paste any outline to add branches)'],
  [`${MOD}+D`, 'Duplicate'], [`${MOD}+A`, 'Select every topic'], [`${MOD}+B / ${MOD}+I`, 'Bold / Italic'], [`${MOD}+K`, 'Add or edit a link'],
  [`${MOD}+F`, 'Find'], [`${MOD}+0 / Shift+1`, 'Zoom to fit'], [`${MOD}+= / ${MOD}+-`, 'Zoom in / out'],
  ['Scroll / Pinch', 'Pan / zoom (Settings can make the wheel zoom)'], ['Drag the canvas / Space+drag', 'Pan'],
  ['Shift+drag the canvas', 'Select several topics'], ['Shift-click', 'Add a topic to the selection'],
  ['Drag a topic', 'Drop it on a topic (child), beside one (reorder) or on empty canvas (floating)'], ['Alt while dropping', 'Copy the branch instead of moving it'],
  ['Double-click the canvas', 'A floating topic there'],
  ['Bubble map: drag a bubble', 'It moves and its neighbours follow (it stays pinned there); drop it on another bubble to put it under that one'],
  ['Bubble map: hover a bubble', 'Its neighbours light up, the rest fades'], ['Escape', 'Step out: menu, find, selection, then focus'],
  ['[ ] or [x] at the start', 'Typed before a topic\'s text, it becomes a task'], ['?', 'This list'],
];

export function shortcutsModal() {
  const m = modal('Keyboard shortcuts', 'Keys work while a mindmap is open. Saving is automatic.', { wide: true });
  const grid = h('div', { class: 'cthd-keys' });
  for (const [k, v] of SHORTCUTS) grid.append(h('span', { class: 'cthd-kbd-wrap' }, ...k.split(' / ').flatMap((part, i) => [i ? h('span', { class: 'cthd-kbd-sep', text: '/' }) : null, h('kbd', { class: 'cthd-kbd', text: part })])), h('span', { class: 'cthd-keys-v', text: v }));
  m.content.append(grid);
  m.footer(button({ label: 'Done', variant: 'default', onClick: () => m.close() }));
  m.open();
}

/** A multi-line text dialog (topic notes). Cmd+Enter saves. Resolves the text, or null. */
export function textModal({ title, value = '', placeholder = '', action = 'Save', readOnly = false, desc = '' }) {
  return new Promise((res) => {
    const m = modal(title, desc, { wide: true });
    const area = h('textarea', { class: 'cthd-input cthw-textarea', rows: '9', placeholder, spellcheck: 'true', 'aria-label': title, readonly: readOnly || null });
    area.value = value;
    const finish = (v) => { m.onClose = null; res(v); m.close(); };
    area.addEventListener('keydown', (e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); finish(area.value); } });
    m.content.append(area);
    if (readOnly) m.footer(button({ label: 'Close', variant: 'default', onClick: () => m.close() }));
    else m.footer(value ? button({ label: 'Remove', variant: 'outline', danger: true, onClick: () => finish('') }) : null, h('span', { class: 'cthd-grow' }), button({ label: 'Cancel', variant: 'outline', onClick: () => m.close() }), button({ label: action, variant: 'default', onClick: () => finish(area.value) }));
    m.onClose = () => res(null);
    m.open();
    setTimeout(() => { area.focus(); area.setSelectionRange(area.value.length, area.value.length); }, 0);
  });
}
